import { iterateFrom } from 'layout-data-lib/GridLayoutDataTree/utils';
import set from 'hs-lodash/set';
import get from 'hs-lodash/get';
import { isModuleNodeValue } from 'layout-data-lib/GridLayoutDataTree/typenameUtils';
import { GRID_NODE_TYPES } from 'layout-data-lib/GridLayoutDataTree/constants';
// @ts-expect-error not typed
import { getIsModuleReducerDiffingEnabled } from './moduleReducerUtils';
import Raven from 'raven-js';
const GENERATED_ID_QUEUES = {
  content: {},
  schema: {}
};
export function createGeneratedIdQueueForGridLayoutTree(contentOrSchema, treeName) {
  set(GENERATED_ID_QUEUES, [contentOrSchema, treeName], {
    [GRID_NODE_TYPES.ROOT]: [],
    [GRID_NODE_TYPES.SECTION]: [],
    [GRID_NODE_TYPES.CONTAINER]: [],
    [GRID_NODE_TYPES.MODULE]: []
  });
}
export function createAddToGeneratedIdQueue(contentOrSchema, treeName) {
  return (type, val) => {
    if (!getIsModuleReducerDiffingEnabled()) {
      return;
    }
    const generatedIdQueue = get(GENERATED_ID_QUEUES, [contentOrSchema, treeName, type], []);
    generatedIdQueue.push(val);
    set(GENERATED_ID_QUEUES, [contentOrSchema, treeName, type], generatedIdQueue);
  };
}
export function createReadFromGeneratedIdQueue(contentOrSchema, treeName) {
  return type => {
    if (!getIsModuleReducerDiffingEnabled()) {
      Raven.captureMessage('Trying to add to node name queue without enabling module reducer diffing.');
      console.error('Trying to add to node name queue without enabling module reducer diffing. Make sure to call enableModuleReducerDiffing before the module reducer is called');
    }
    const list = get(GENERATED_ID_QUEUES, [contentOrSchema, treeName, type], []);
    return list.shift();
  };
}
export function getGridLayoutTreeToModify(state, gridLayoutId) {
  var _state$getIn;
  return (_state$getIn = state.getIn(['gridLayoutTrees', gridLayoutId])) !== null && _state$getIn !== void 0 ? _state$getIn : state.getIn(['schemaGridLayoutTrees', gridLayoutId]);
}
function copyClonedModuleToGridLayoutModule(state, rootId, moduleNode, clonedIdToOriginalIdMap) {
  const clonedNodeId = moduleNode.getId();
  const originalNodeId = clonedIdToOriginalIdMap[clonedNodeId];
  const gridLayoutModule = state.getIn(['gridLayoutModules', rootId]).find(module => module.get('name') === originalNodeId);
  if (gridLayoutModule) {
    const newModule = gridLayoutModule.merge({
      id: clonedNodeId,
      name: clonedNodeId
    });
    return state.setIn(['gridLayoutModules', rootId], state.getIn(['gridLayoutModules', rootId]).push(newModule));
  }
  return state;
}
export function copyClonedModulesToGridLayoutModules(state, clonedNode, clonedIdToOriginalIdMap) {
  let currentState = state;
  const rootId = clonedNode.getRootId();
  currentState = copyGridLayoutFromSchemaToContentIfNeeded(currentState, rootId);
  if (clonedNode.isModule()) {
    currentState = copyClonedModuleToGridLayoutModule(currentState, rootId, clonedNode, clonedIdToOriginalIdMap);
  } else {
    iterateFrom(clonedNode, {
      includeStartNode: false,
      gridNodeCallback: ({
        gridNode
      }) => {
        if (gridNode.isModule()) {
          currentState = copyClonedModuleToGridLayoutModule(currentState, rootId, gridNode, clonedIdToOriginalIdMap);
        }
      }
    });
  }
  return currentState;
}
export function copyGridLayoutFromSchemaToContent(state, gridLayoutId) {
  const tree = state.getIn(['schemaGridLayoutTrees', gridLayoutId]);
  return state.setIn(['gridLayoutTrees', gridLayoutId], tree);
}
export function copyGridLayoutFromSchemaToContentIfNeeded(state, gridLayoutId) {
  if (!state.getIn(['gridLayoutTrees', gridLayoutId])) {
    return copyGridLayoutFromSchemaToContent(state, gridLayoutId);
  }
  return state;
}
export function getGridLayoutTreeToModifyImmer(draft, treeName) {
  var _draft$gridLayoutTree;
  return (_draft$gridLayoutTree = draft.gridLayoutTrees[treeName]) !== null && _draft$gridLayoutTree !== void 0 ? _draft$gridLayoutTree : draft.schemaGridLayoutTrees[treeName];
}
function copyClonedModuleToGridLayoutModuleImmer(draft, rootId, moduleNode, clonedIdToOriginalIdMap) {
  const clonedNodeId = moduleNode.getId();
  const originalNodeId = clonedIdToOriginalIdMap[clonedNodeId];
  const gridLayoutModule = draft.gridLayoutModules[rootId].find(module => module.name === originalNodeId);
  if (gridLayoutModule) {
    const newModule = Object.assign({}, gridLayoutModule, {
      id: clonedNodeId,
      name: clonedNodeId
    });
    draft.gridLayoutModules[rootId].push(newModule);
  }
}
export function copyClonedModulesToGridLayoutModulesImmer(draft, clonedNode, clonedIdToOriginalIdMap) {
  const rootId = clonedNode.getRootId();
  copyGridLayoutFromSchemaToContentIfNeededImmer(draft, rootId);
  if (clonedNode.isModule()) {
    copyClonedModuleToGridLayoutModuleImmer(draft, rootId, clonedNode, clonedIdToOriginalIdMap);
  } else {
    iterateFrom(clonedNode, {
      includeStartNode: false,
      gridNodeCallback: ({
        gridNode
      }) => {
        if (gridNode.isModule()) {
          copyClonedModuleToGridLayoutModuleImmer(draft, rootId, gridNode, clonedIdToOriginalIdMap);
        }
      }
    });
  }
}
export function copyGridLayoutFromSchemaToContentImmer(draft, gridLayoutId) {
  const tree = get(draft, ['schemaGridLayoutTrees', gridLayoutId]);
  set(draft, ['gridLayoutTrees', gridLayoutId], tree);
}
export function copyGridLayoutFromSchemaToContentIfNeededImmer(draft, gridLayoutId) {
  if (!get(draft, ['gridLayoutTrees', gridLayoutId])) {
    copyGridLayoutFromSchemaToContentImmer(draft, gridLayoutId);
  }
}

/**
 * We hydrate missing properties in the grid layout tree since our Grid model is
 * minimal and decoupled from Content as much as possible. We should only have
 * to do this once on load, for each schema tree.
 */
export function getHydrateMissingPropertiesInGridLayoutTree(gridLayoutModules, gridLayoutId) {
  return nodeValue => {
    if (isModuleNodeValue(nodeValue)) {
      const module = gridLayoutModules[gridLayoutId].find(m => m.name === nodeValue.id);
      if (module) {
        nodeValue.label = module.label;
        nodeValue.moduleId = module.module_id;
      }
    }
    return nodeValue;
  };
}
const prepareNewGridModuleData = newModuleSchemaJson => {
  if (newModuleSchemaJson) {
    var _newModuleData$jsModu, _newModuleData$path, _newModuleData$fieldO;
    const newModuleData = Object.assign({}, newModuleSchemaJson);
    newModuleData.jsModule = (_newModuleData$jsModu = newModuleData.jsModule) !== null && _newModuleData$jsModu !== void 0 ? _newModuleData$jsModu : false;
    newModuleData.moduleId = newModuleData.module_id;
    newModuleData.path = (_newModuleData$path = newModuleData.path) !== null && _newModuleData$path !== void 0 ? _newModuleData$path : (_newModuleData$fieldO = newModuleData.fieldOverrides) === null || _newModuleData$fieldO === void 0 ? void 0 : _newModuleData$fieldO.path;
    newModuleData.type = GRID_NODE_TYPES.MODULE;
    newModuleData.styles = {};
    delete newModuleData.module_id;
    delete newModuleData.body;
    return newModuleData;
  }
  return null;
};
export const prepareNewGridModuleDataHelper = newModuleSchemaJson => {
  return prepareNewGridModuleData(newModuleSchemaJson);
};
export function initializeGridLayoutModules(schemaGridLayouts) {
  const gridLayoutModules = {};
  Object.keys(schemaGridLayouts !== null && schemaGridLayouts !== void 0 ? schemaGridLayouts : {}).forEach(gridLayoutId => {
    gridLayoutModules[gridLayoutId] = [];
  });
  return gridLayoutModules;
}